/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {KTIcon} from '../../../helpers'
import {
  getAccountPermissions,
  switchAccount,
  switchBackToAdmin,
  toggleAdminPermissionMode,
} from '../../../../redux/action/account'

const AccountSwitcher: FC = () => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)

  // Redux state
  const {
    availableAccounts,
    activeAccountId,
    activeAccountType,
    userPermissions,
    userRole,
    adminPermissionMode,
    originalUserId,
    loading,
  } = useSelector((state: any) => state.Account)

  const userdata = useSelector((state: any) => state.Auth?.authProfile?.userData)
  const isAdminMode = localStorage.getItem('isAdminMode') === 'true'
  const originalAdminId = localStorage.getItem('originalAdminId')
  const localAdminPermissionMode = localStorage.getItem('adminPermissionMode') === 'true'

  // Debug Redux state changes
  useEffect(() => {
    console.log('AccountSwitcher Redux state changed:', {
      availableAccounts: availableAccounts,
      'availableAccounts.length': availableAccounts.length,
      activeAccountId: activeAccountId,
      activeAccountType: activeAccountType,
      userPermissions: userPermissions,
    })
  }, [availableAccounts, activeAccountId, activeAccountType, loading])

  useEffect(() => {
    // Load available accounts on component mount
    // Use userdata?.uid if available, otherwise fall back to localStorage userId
    const effectiveUserId = userdata?.uid || localStorage.getItem('userId')

    if (effectiveUserId) {
      console.log('AccountSwitcher: Calling getAccountPermissions for user:', effectiveUserId)
      dispatch(getAccountPermissions())
    } else {
      console.log('AccountSwitcher: NOT calling getAccountPermissions - no userId available')
      console.log('Details:', {
        'userdata?.uid': userdata?.uid,
        'localStorage.userId': localStorage.getItem('userId'),
      })
    }
  }, [dispatch, userdata?.uid, userdata])

  const handleAccountSwitch = async (accountId: string, accountType: 'personal' | 'b2b') => {
    try {
      console.log('AccountSwitcher: handleAccountSwitch called with:', {accountId, accountType})
      await dispatch(switchAccount({accountId, accountType}))
      setIsOpen(false)
      // Refresh page to load new account context
      window.location.reload()
    } catch (error) {
      console.error('Failed to switch account:', error)
    }
  }

  const handleBackToAdmin = async () => {
    try {
      await dispatch(switchBackToAdmin())
      setIsOpen(false)
      window.location.replace('/tools')
    } catch (error) {
      console.error('Failed to return to admin:', error)
    }
  }

  const getCurrentAccountName = () => {
    const {activeAccountId: currentActiveId, activeAccountType: currentActiveType} =
      getCurrentActiveAccount()

    if (isAdminMode) {
      const permissionMode = localAdminPermissionMode ? 'Admin Permissions' : 'User Permissions'
      if (currentActiveType === 'b2b') {
        const b2bAccount = availableAccounts.find((acc: any) => acc.accountId === currentActiveId)
        return `${b2bAccount?.businessName || 'B2B Account'} (${permissionMode})`
      }
      return `User Account (${permissionMode})`
    }

    if (currentActiveType === 'b2b') {
      const b2bAccount = availableAccounts.find((acc: any) => acc.accountId === currentActiveId)
      return b2bAccount?.businessName || 'Business Account'
    }

    return 'Personal Account'
  }

  const getRoleText = () => {
    if (isAdminMode) return 'Administrator'
    if (userRole === 'primary') return 'Primary User'
    if (userRole === 'sub-user') return 'Sub User'
    return 'Owner'
  }

  const handleToggleAdminPermissions = async () => {
    try {
      await dispatch(toggleAdminPermissionMode())
      // Force re-render to reflect localStorage changes
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to toggle admin permissions:', error)
    }
  }

  // Get the correct personal account ID
  const getPersonalAccountId = () => {
    // Check if we have an originalUserId in localStorage (from account switching)
    const localOriginalUserId = localStorage.getItem('originalUserId')

    // For personal account ID, prioritize:
    // 1. originalUserId from localStorage (if we switched from personal to B2B)
    // 2. userdata?.uid (the authenticated user's ID)
    // 3. currentUserId from localStorage as fallback
    const personalAccountId = localOriginalUserId || userdata?.uid || localStorage.getItem('userId')

    console.log('AccountSwitcher: getPersonalAccountId determined:', {
      localOriginalUserId,
      'userdata?.uid': userdata?.uid,
      'localStorage.userId': localStorage.getItem('userId'),
      personalAccountId,
    })

    return personalAccountId
  }

  // Get current account info from localStorage (reliable after page reload)
  const getActiveAccountFromStorage = () => {
    return {
      activeAccountId: localStorage.getItem('activeAccountId'),
      activeAccountType: localStorage.getItem('activeAccountType') || 'personal',
    }
  }

  // Get the current active account (prefer Redux if loaded, fallback to localStorage)
  const getCurrentActiveAccount = () => {
    // If Redux state is loaded and has valid data, use it
    if (activeAccountId && activeAccountType) {
      console.log('AccountSwitcher: Using Redux state for active account:', {
        activeAccountId,
        activeAccountType,
      })
      return {activeAccountId, activeAccountType}
    }
    // Otherwise use localStorage values
    const storageValues = getActiveAccountFromStorage()
    console.log('AccountSwitcher: Using localStorage for active account:', storageValues)
    return storageValues
  }

  return (
    <div className='d-flex align-items-center gap-3'>
      {/* Only show AccountSwitcher if there are multiple accounts OR in admin mode */}
      {availableAccounts.length > 0 || isAdminMode || activeAccountType === 'b2b' ? (
        <>
          {/* Admin Permission Toggle - Always visible when in admin mode */}
          {isAdminMode && (
            <div className='d-flex align-items-center bg-light-warning rounded p-2 shadow-sm'>
              <div className='d-flex flex-column me-3'>
                <span className='text-dark fw-bold fs-8'>Admin View Mode</span>
                <span className='text-muted fs-9'>
                  {localAdminPermissionMode ? 'Using Admin Permissions' : 'Using User Permissions'}
                </span>
              </div>
              <button
                className={`btn btn-sm ${
                  localAdminPermissionMode ? 'btn-warning' : 'btn-light-primary'
                } fw-bold d-flex align-items-center`}
                onClick={handleToggleAdminPermissions}
                disabled={loading}
                title={localAdminPermissionMode ? 'Switch to User View' : 'Switch to Admin View'}
              >
                <KTIcon iconName='switch' className='fs-6 me-1' />
                {localAdminPermissionMode ? 'Admin' : 'User'}
              </button>
            </div>
          )}

          <div className='position-relative'>
            <button
              className='btn account-switcher-btn btn-sm btn-light-primary fw-bold d-flex align-items-center'
              onClick={() => setIsOpen(!isOpen)}
              type='button'
            >
              <KTIcon iconName='profile-circle' className='fs-4 me-2' />
              <div className='d-flex flex-column align-items-start'>
                <span className='text-dark fw-bold fs-7'>{getCurrentAccountName()}</span>
                <span className='text-muted fs-8'>{getRoleText()}</span>
              </div>
              <KTIcon iconName='down' className='fs-4 ms-2' />
            </button>

            {isOpen && (
              <div
                className='menu menu-column bg-secondary-subtle menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-300px position-absolute top-100 start-0 mt-2'
                style={{zIndex: 1050, display: 'block !important'}}
              >
                <div className='menu-item px-3'>
                  <div className='menu-content text-muted pb-2 text-uppercase fs-7'>
                    Available Accounts
                  </div>
                </div>

                {/* Personal Account */}
                <div className='menu-item px-3'>
                  <a
                    href='#'
                    className={`menu-link px-3 ${(() => {
                      const {
                        activeAccountId: currentActiveId,
                        activeAccountType: currentActiveType,
                      } = getCurrentActiveAccount()
                      const personalAccountId = getPersonalAccountId()
                      return currentActiveId === personalAccountId &&
                        currentActiveType === 'personal'
                        ? 'active'
                        : ''
                    })()}`}
                    onClick={(e) => {
                      e.preventDefault()
                      const personalAccountId = getPersonalAccountId()
                      const {
                        activeAccountId: currentActiveId,
                        activeAccountType: currentActiveType,
                      } = getCurrentActiveAccount()
                      if (
                        currentActiveId !== personalAccountId ||
                        currentActiveType !== 'personal'
                      ) {
                        handleAccountSwitch(personalAccountId, 'personal')
                      }
                    }}
                  >
                    <div className='d-flex align-items-center'>
                      <KTIcon iconName='profile-circle' className='fs-4 me-3' />
                      <div className='d-flex flex-column'>
                        <span className='fw-bold'>Personal Account</span>
                        <span className='text-muted fs-7'>Owner</span>
                      </div>
                      {(() => {
                        const {
                          activeAccountId: currentActiveId,
                          activeAccountType: currentActiveType,
                        } = getCurrentActiveAccount()
                        const personalAccountId = getPersonalAccountId()
                        return (
                          currentActiveId === personalAccountId &&
                          currentActiveType === 'personal' && (
                            <KTIcon iconName='check' className='fs-4 ms-auto text-primary' />
                          )
                        )
                      })()}
                    </div>
                  </a>
                </div>

                {/* B2B Accounts */}
                {availableAccounts.map((account: any) => (
                  <div key={account.accountId} className='menu-item px-3'>
                    <a
                      href='#'
                      className={`menu-link px-3 ${(() => {
                        const {activeAccountId: currentActiveId} = getCurrentActiveAccount()
                        return currentActiveId === account.accountId ? 'active' : ''
                      })()}`}
                      onClick={(e) => {
                        e.preventDefault()
                        const {activeAccountId: currentActiveId} = getCurrentActiveAccount()
                        if (currentActiveId !== account.accountId) {
                          handleAccountSwitch(account.accountId, 'b2b')
                        }
                      }}
                    >
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='bank' className='fs-4 me-3' />
                        <div className='d-flex flex-column'>
                          <span className='fw-bold'>{account.businessName}</span>
                          <span className='text-muted fs-7'>
                            {account.role === 'primary' ? 'Primary User' : 'Sub User'}
                          </span>
                        </div>
                        {(() => {
                          const {activeAccountId: currentActiveId} = getCurrentActiveAccount()
                          return (
                            currentActiveId === account.accountId && (
                              <KTIcon iconName='check' className='fs-4 ms-auto text-primary' />
                            )
                          )
                        })()}
                      </div>
                    </a>
                  </div>
                ))}

                {/* Admin Mode Controls */}
                {isAdminMode && originalAdminId && (
                  <>
                    <div className='separator my-3'></div>
                    <div className='menu-item px-3'>
                      <a
                        href='#'
                        className='menu-link px-3 text-danger'
                        onClick={(e) => {
                          e.preventDefault()
                          handleBackToAdmin()
                        }}
                      >
                        <KTIcon iconName='exit-left' className='fs-4 me-3' />
                        <span>Back to Admin Account</span>
                      </a>
                    </div>
                  </>
                )}

                {/* Permissions Info */}
                {(() => {
                  const {activeAccountType: currentActiveType} = getCurrentActiveAccount()
                  return (
                    currentActiveType === 'b2b' && (
                      <>
                        <div className='separator my-3'></div>
                        <div className='menu-item px-3'>
                          <div className='menu-content text-muted pb-2 text-uppercase fs-7'>
                            Current Permissions
                          </div>
                        </div>
                        <div className='menu-item px-3'>
                          <div className='menu-content'>
                            <div className='d-flex flex-wrap gap-2'>
                              {Object.entries(userPermissions || {}).map(([key, value]) =>
                                value ? (
                                  <span
                                    key={key}
                                    className='badge badge-light-primary fw-bold fs-8'
                                  >
                                    {key
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </span>
                                ) : null
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  )
                })()}
              </div>
            )}

            {/* Overlay to close dropdown */}
            {isOpen && (
              <div
                className='position-fixed top-0 start-0 w-100 h-100'
                style={{zIndex: 1040}}
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

export default AccountSwitcher
